import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { t as translate, DEFAULT_LANG, isLangLoaded, loadLang } from '@bookingdei/i18n/translate';
import type { Lang } from '@bookingdei/i18n/translate';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import { fillTokens, type MessageVars } from './fill-tokens';

// Holds the en/bn choice for the whole surface (DR-16: i18n is a bundled asset, no
// API). `t` is pre-bound to the active language so screens call `t('home.featured')`.
interface LanguageContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: MessageKey, vars?: MessageVars) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);
const STORAGE_KEY = 'bd.lang';
// How long a saved Bangla choice may hold the first paint before English shows.
const DICTIONARY_WAIT_MS = 2500;

function readInitialLang(): Lang {
  if (typeof localStorage !== 'undefined') {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'en' || saved === 'bn') return saved;
  }
  return DEFAULT_LANG;
}

// Bangla is its own chunk (translate.ts). Start fetching it as soon as the app
// loads when it is the saved choice, so the first paint is not held for long.
const savedLang = readInitialLang();
if (!isLangLoaded(savedLang)) loadLang(savedLang).catch(() => undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(readInitialLang);
  const [, setLoaded] = useState(0);
  // Never a blank page: if the dictionary fails or is slow, paint English (the
  // fallback `t` already uses) and switch to Bangla the moment it arrives.
  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => {
    if (isLangLoaded(lang)) return;
    const timer = setTimeout(() => setGaveUp(true), DICTIONARY_WAIT_MS);
    loadLang(lang).then(
      () => setLoaded((n) => n + 1),
      () => setGaveUp(true),
    );
    return () => clearTimeout(timer);
  }, [lang]);

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next: Lang) => {
    const apply = () => {
      setLangState(next);
      if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, next);
    };
    // Switch only once the dictionary is there, so the page never flashes English.
    if (isLangLoaded(next)) apply();
    else {
      // Saved first: if the dictionary chunk is gone after a deploy, the tab reloads
      // (shell/reload-on-stale-chunk.ts) and opens in the language just picked.
      if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, next);
      loadLang(next).then(apply, () => undefined);
    }
  }, []);

  const value = useMemo<LanguageContextValue>(
    () => ({ lang, setLang, t: (key, vars) => fillTokens(translate(lang, key), lang, vars) }),
    [lang, setLang],
  );

  // A saved Bangla choice waits for its dictionary instead of painting English first.
  if (!isLangLoaded(lang) && !gaveUp) return null;
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within a LanguageProvider');
  return ctx;
}
